/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * API Client Helpers - Utility functions for API request/response handling
 */

import { notifyRateLimited } from "@/lib/api/rate-limit-events";
import { logger } from "../utils/logger";
import type {
  ApiFailure,
  ApiResponseEnvelope,
  ApiSuccess,
} from "./api-client-types";

// ============================================================================
// RESPONSE PARSING
// ============================================================================

/**
 * Parse Retry-After header value to seconds
 */
export function parseRetryAfter(
  headerValue: string | null,
): number | undefined {
  if (!headerValue) return undefined;
  const n = Number(headerValue);
  if (Number.isFinite(n)) {
    return n;
  }

  const retryAtMs = Date.parse(headerValue);
  if (Number.isNaN(retryAtMs)) {
    return undefined;
  }

  return Math.max(0, Math.ceil((retryAtMs - Date.now()) / 1000));
}

/**
 * Safely parse JSON from a response
 */
export async function parseJsonSafe<T>(res: Response): Promise<T | null> {
  try {
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function toApiFailure(
  status: number,
  payload: {
    message: string;
    code?: string;
    details?: Record<string, unknown>;
    requestId?: string;
    retryAfterSeconds?: number;
  },
): ApiFailure {
  return {
    ok: false,
    status,
    message: payload.message,
    ...(payload.code ? { code: payload.code } : {}),
    ...(payload.details ? { details: payload.details } : {}),
    ...(payload.requestId ? { requestId: payload.requestId } : {}),
    ...(payload.retryAfterSeconds
      ? { retryAfterSeconds: payload.retryAfterSeconds }
      : {}),
  };
}

// ============================================================================
// RESPONSE BUILDING
// ============================================================================

/**
 * Build a success response from a fetch Response.
 * Auto-unwraps the standard API envelope ({ success, data, message? })
 * so consumers get result.data directly instead of result.data.data.
 */
export async function buildSuccessResponse<T>(
  res: Response,
  requestId: string | undefined,
): Promise<ApiSuccess<T> | ApiFailure> {
  if (res.status === 204) {
    const noContentResult: ApiSuccess<T> = {
      ok: true,
      status: res.status,
      data: undefined as T,
      ...(requestId ? { requestId } : {}),
    };
    return noContentResult;
  }

  const json = await parseJsonSafe<ApiResponseEnvelope<T>>(res);
  if (json === null) {
    return toApiFailure(res.status, {
      message: "Unable to parse API response body",
      code: "PARSE_ERROR",
      requestId,
    });
  }

  // Detect standard API envelope: { success?, data?, error?, message? }
  const isEnvelope =
    isObjectRecord(json) &&
    ("data" in json ||
      "error" in json ||
      "message" in json ||
      "success" in json);

  if (isEnvelope) {
    const envelope = json as ApiResponseEnvelope<T>;
    // If envelope says success: false despite HTTP 200, treat as failure
    if (envelope.success === false) {
      const errorDetails = envelope.error;
      return toApiFailure(res.status, {
        message: errorDetails?.message ?? envelope.message ?? "Request failed",
        code: errorDetails?.code,
        details: errorDetails?.details ?? envelope.details,
        requestId,
      });
    }

    const successResult: ApiSuccess<T> = {
      ok: true,
      status: res.status,
      data: envelope.data as T,
      ...(envelope.message ? { message: envelope.message } : {}),
      ...(envelope.pagination ? { pagination: envelope.pagination } : {}),
      ...(envelope.metrics !== undefined ? { metrics: envelope.metrics } : {}),
      ...(envelope.facets !== undefined ? { facets: envelope.facets } : {}),
      ...(requestId ? { requestId } : {}),
    };
    return successResult;
  }

  // Non-envelope response (external APIs, raw payloads)
  const rawSuccess: ApiSuccess<T> = {
    ok: true,
    status: res.status,
    data: json as T,
    ...(requestId ? { requestId } : {}),
  };
  return rawSuccess;
}

/**
 * Build a failure response from a fetch Response
 */
export async function buildFailureResponse(
  res: Response,
  requestId: string | undefined,
  retryAfterSeconds: number | undefined,
): Promise<ApiFailure> {
  const errJson = await parseJsonSafe<ApiResponseEnvelope>(res);
  const errorDetails = errJson?.error;
  const details = errorDetails?.details ?? errJson?.details;

  return toApiFailure(res.status, {
    message:
      errorDetails?.message ??
      errJson?.message ??
      `${res.status} ${res.statusText}`,
    code: errorDetails?.code,
    details,
    requestId,
    retryAfterSeconds,
  });
}

// ============================================================================
// ERROR RESPONSES
// ============================================================================

/**
 * Create an unauthorized failure response
 */
export function createUnauthorizedResponse(): ApiFailure {
  return {
    ok: false,
    status: 401,
    message:
      "Authentication token unavailable. Please wait a moment and try again, or refresh the page.",
    code: "UNAUTHORIZED",
    details: {
      reason: "token_timeout_or_missing",
      suggestion:
        "The authentication token may be loading. Please try again in a moment.",
    },
  };
}

/**
 * Create a timeout failure response
 */
export function createTimeoutResponse(): ApiFailure {
  return {
    ok: false,
    status: 408,
    message: "Request timeout",
    code: "TIMEOUT",
  };
}

/**
 * Create a network error failure response
 */
export function createNetworkErrorResponse(): ApiFailure {
  return {
    ok: false,
    status: 0,
    message: "Network error",
    code: "NETWORK_ERROR",
  };
}

// ============================================================================
// RATE LIMIT HANDLING
// ============================================================================

/**
 * Handle 429 rate limit response - notify listeners
 */
export function handleRateLimitResponse(
  res: Response,
  requestId: string | undefined,
  retryAfterSeconds: number | undefined,
): void {
  const parseOptionalNumber = (value: string | null): number | null => {
    if (!value) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  try {
    const remainingRaw =
      res.headers.get("X-RateLimit-Remaining") ??
      res.headers.get("x-ratelimit-remaining");
    const resetRaw =
      res.headers.get("X-RateLimit-Reset") ??
      res.headers.get("x-ratelimit-reset");

    notifyRateLimited({
      status: 429,
      retryAfterSeconds: retryAfterSeconds ?? null,
      rateLimitRemaining: parseOptionalNumber(remainingRaw),
      rateLimitReset: parseOptionalNumber(resetRaw),
      requestId: requestId ?? null,
      at: Date.now(),
    });
  } catch (error) {
    logger.error("Failed to notify rate limit listeners:", error);
  }
}

// ============================================================================
// REQUEST ID EXTRACTION
// ============================================================================

/**
 * Extract request ID from response headers
 */
export function extractRequestId(res: Response): string | undefined {
  return (
    res.headers.get("X-Request-ID") ??
    res.headers.get("x-request-id") ??
    undefined
  );
}
